import type { FieldType } from './types';

export function drawSoccerField(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  fieldType: FieldType = 'full'
) {
  // Outer background (lighter green, matching field stripes)
  ctx.fillStyle = '#4a9e4a';
  ctx.fillRect(0, 0, width, height);

  const px = 22;       // left/right padding
  const padTop = 38;   // extra top padding for action bar
  const padBot = 22;   // bottom padding
  const fw = width - px * 2;

  // Futsal is a 40x20 court, not a 105x68 pitch, and it is played indoors on a
  // hard surface. Drawing it at the outdoor pitch's proportions on mown grass
  // would make the one preset whose whole point is that it is a different
  // surface look identical to the others, so it gets its own height and its
  // own floor.
  if (fieldType === 'futsal') {
    const courtH = fw / 2;                       // the real 2:1 ratio
    const py = padTop + (height - padTop - padBot - courtH) / 2;

    ctx.fillStyle = '#2f6b46';                   // surround
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#3f7f5c';                   // court floor, flat and matt
    ctx.fillRect(px, py, fw, courtH);

    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    ctx.strokeRect(px, py, fw, courtH);
    drawFutsalCourt(ctx, px, py, fw, courtH);
    return;
  }

  const fh = height - padTop - padBot;

  // Field green with vertical stripes
  const stripeCount = 16;
  const stripeW = fw / stripeCount;
  for (let i = 0; i < stripeCount; i++) {
    ctx.fillStyle = i % 2 === 0 ? '#4a9e4a' : '#55b455';
    ctx.fillRect(px + i * stripeW, padTop, stripeW + 0.5, fh);
  }

  // Field lines
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 2;
  ctx.setLineDash([]);

  // Outer boundary
  ctx.strokeRect(px, padTop, fw, fh);

  switch (fieldType) {
    case 'full':
      drawFullField(ctx, px, padTop, fw, fh);
      break;
    case 'half':
      drawHalfField(ctx, px, padTop, fw, fh);
      break;
    case 'full-zones':
      drawFullZonesField(ctx, px, padTop, fw, fh);
      break;
    case 'three-quarter':
      drawThreeQuarterField(ctx, px, padTop, fw, fh);
      break;
    case 'full-v-thirds':
      drawFullVThirdsField(ctx, px, padTop, fw, fh);
      break;
    case 'full-h-zones':
      drawFullHZonesField(ctx, px, padTop, fw, fh);
      break;
    case 'full-grid':
      drawFullGridField(ctx, px, padTop, fw, fh);
      break;
    case 'full-h-corridors':
      drawFullHCorridorsField(ctx, px, padTop, fw, fh);
      break;
  }
}

function drawFullField(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  // Center line (vertical)
  ctx.beginPath();
  ctx.moveTo(px + fw / 2, py);
  ctx.lineTo(px + fw / 2, py + fh);
  ctx.stroke();

  // Center circle
  const centerR = fh * 0.2;
  ctx.beginPath();
  ctx.arc(px + fw / 2, py + fh / 2, centerR, 0, Math.PI * 2);
  ctx.stroke();

  // Center spot
  ctx.beginPath();
  ctx.arc(px + fw / 2, py + fh / 2, 3, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fill();

  // --- LEFT SIDE ---
  // Penalty area
  const paH = fh * 0.62;
  const paW = fw * 0.16;
  ctx.strokeRect(px, py + (fh - paH) / 2, paW, paH);

  // Goal area
  const gaH = fh * 0.3;
  const gaW = fw * 0.06;
  ctx.strokeRect(px, py + (fh - gaH) / 2, gaW, gaH);

  // Penalty spot
  const penSpotLX = px + paW * 0.72;
  ctx.beginPath();
  ctx.arc(penSpotLX, py + fh / 2, 3, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fill();

  // Penalty arc – small arc centered on penalty spot, touching the box edge
  const distToEdgeL = (px + paW) - penSpotLX;
  const penArcR = distToEdgeL * 1.8;
  const penArcAngleL = Math.acos(distToEdgeL / penArcR);
  ctx.beginPath();
  ctx.arc(penSpotLX, py + fh / 2, penArcR, -penArcAngleL, penArcAngleL);
  ctx.stroke();

  // Left goal (net)
  const goalH = fh * 0.14;
  const goalW = 12;
  drawGoalNet(ctx, px - goalW, py + (fh - goalH) / 2, goalW, goalH);

  // --- RIGHT SIDE ---
  ctx.strokeRect(px + fw - paW, py + (fh - paH) / 2, paW, paH);
  ctx.strokeRect(px + fw - gaW, py + (fh - gaH) / 2, gaW, gaH);

  // Right penalty spot
  const penSpotRX = px + fw - paW * 0.72;
  ctx.beginPath();
  ctx.arc(penSpotRX, py + fh / 2, 3, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fill();

  // Right penalty arc – small arc centered on penalty spot, touching box edge
  const distToEdgeR = penSpotRX - (px + fw - paW);
  const penArcRR = distToEdgeR * 1.8;
  const penArcAngleR = Math.acos(distToEdgeR / penArcRR);
  ctx.beginPath();
  ctx.arc(penSpotRX, py + fh / 2, penArcRR, Math.PI - penArcAngleR, Math.PI + penArcAngleR);
  ctx.stroke();

  drawGoalNet(ctx, px + fw, py + (fh - goalH) / 2, goalW, goalH);

  // Corner arcs
  const cornerR = 10;
  ctx.beginPath(); ctx.arc(px, py, cornerR, 0, Math.PI * 0.5); ctx.stroke();
  ctx.beginPath(); ctx.arc(px + fw, py, cornerR, Math.PI * 0.5, Math.PI); ctx.stroke();
  ctx.beginPath(); ctx.arc(px, py + fh, cornerR, Math.PI * 1.5, Math.PI * 2); ctx.stroke();
  ctx.beginPath(); ctx.arc(px + fw, py + fh, cornerR, Math.PI, Math.PI * 1.5); ctx.stroke();
}

function drawHalfField(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  const centerR = fh * 0.2;

  // Half center circle at right
  ctx.beginPath();
  ctx.arc(px + fw, py + fh / 2, centerR, 0.5 * Math.PI, 1.5 * Math.PI);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(px + fw, py + fh / 2, 3, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fill();

  // Penalty area
  const paH = fh * 0.62;
  const paW = fw * 0.25;
  ctx.strokeRect(px, py + (fh - paH) / 2, paW, paH);

  // Goal area
  const gaH = fh * 0.3;
  const gaW = fw * 0.09;
  ctx.strokeRect(px, py + (fh - gaH) / 2, gaW, gaH);

  // Penalty spot
  const halfPenSpotX = px + paW * 0.72;
  ctx.beginPath();
  ctx.arc(halfPenSpotX, py + fh / 2, 3, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fill();

  // Penalty arc – small arc centered on penalty spot, touching box edge
  const halfDistToEdge = (px + paW) - halfPenSpotX;
  const halfPenArcR = halfDistToEdge * 1.8;
  const halfPenArcAngle = Math.acos(halfDistToEdge / halfPenArcR);
  ctx.beginPath();
  ctx.arc(halfPenSpotX, py + fh / 2, halfPenArcR, -halfPenArcAngle, halfPenArcAngle);
  ctx.stroke();

  // Goal
  const goalH = fh * 0.14;
  const goalW = 12;
  drawGoalNet(ctx, px - goalW, py + (fh - goalH) / 2, goalW, goalH);

  // Corner arcs
  const cornerR = 10;
  ctx.beginPath(); ctx.arc(px, py, cornerR, 0, Math.PI * 0.5); ctx.stroke();
  ctx.beginPath(); ctx.arc(px, py + fh, cornerR, Math.PI * 1.5, Math.PI * 2); ctx.stroke();
}

function drawFullZonesField(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  // Draw regular full field first
  drawFullField(ctx, px, py, fw, fh);

  // Add dashed zone lines (dividing field into quarters/thirds)
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([10, 8]);

  // Vertical dashed lines at 1/3 and 2/3 (3 equal zones)
  ctx.beginPath();
  ctx.moveTo(px + fw / 3, py);
  ctx.lineTo(px + fw / 3, py + fh);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(px + (fw * 2) / 3, py);
  ctx.lineTo(px + (fw * 2) / 3, py + fh);
  ctx.stroke();

  // Horizontal dashed lines at 1/4 and 3/4
  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.25);
  ctx.lineTo(px + fw, py + fh * 0.25);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.75);
  ctx.lineTo(px + fw, py + fh * 0.75);
  ctx.stroke();

  // Reset line dash
  ctx.setLineDash([]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
}

function drawThreeQuarterField(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  // Shows 2/3 of a full pitch (like full-zones field with right third removed).
  // Visible width = 2/3 of actual pitch, so:
  //   pitch center (50%) → 50/66.67 = 75% of visible width
  //   pitch 1/3 zone      → 33/66.67 = 50% of visible width
  //   pitch 2/3 zone      → coincides with right boundary (100%)

  // --- Left penalty area ---
  const paH = fh * 0.62;
  const paW = fw * 0.24; // 16% of full pitch → 24% of 2/3 visible
  ctx.strokeRect(px, py + (fh - paH) / 2, paW, paH);

  const gaH = fh * 0.3;
  const gaW = fw * 0.09;
  ctx.strokeRect(px, py + (fh - gaH) / 2, gaW, gaH);

  // Penalty spot
  const penSpotX = px + paW * 0.72;
  ctx.beginPath();
  ctx.arc(penSpotX, py + fh / 2, 3, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fill();

  // Penalty arc
  const distToEdge = (px + paW) - penSpotX;
  const penArcR = distToEdge * 1.8;
  const penArcAngle = Math.acos(distToEdge / penArcR);
  ctx.beginPath();
  ctx.arc(penSpotX, py + fh / 2, penArcR, -penArcAngle, penArcAngle);
  ctx.stroke();

  // Left goal net
  const goalH = fh * 0.14;
  const goalW = 12;
  drawGoalNet(ctx, px - goalW, py + (fh - goalH) / 2, goalW, goalH);

  // --- Center line at 75% of visible width (= actual pitch center) ---
  const centerX = px + fw * 0.75;
  ctx.beginPath();
  ctx.moveTo(centerX, py);
  ctx.lineTo(centerX, py + fh);
  ctx.stroke();

  // Center circle
  const centerR = fh * 0.2;
  ctx.beginPath();
  ctx.arc(centerX, py + fh / 2, centerR, 0, Math.PI * 2);
  ctx.stroke();

  // Center spot
  ctx.beginPath();
  ctx.arc(centerX, py + fh / 2, 3, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fill();

  // --- Dashed zone lines (same as full-zones, clipped to 2/3) ---
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([10, 8]);

  // Vertical dashed line at 1/3 of pitch = 50% of visible width
  const zone1X = px + fw * 0.5;
  ctx.beginPath();
  ctx.moveTo(zone1X, py);
  ctx.lineTo(zone1X, py + fh);
  ctx.stroke();

  // Horizontal dashed lines at 1/4 and 3/4 height
  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.25);
  ctx.lineTo(px + fw, py + fh * 0.25);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.75);
  ctx.lineTo(px + fw, py + fh * 0.75);
  ctx.stroke();

  // Reset
  ctx.setLineDash([]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';

  // Corner arcs (left side only)
  const cornerR = 10;
  ctx.beginPath(); ctx.arc(px, py, cornerR, 0, Math.PI * 0.5); ctx.stroke();
  ctx.beginPath(); ctx.arc(px, py + fh, cornerR, Math.PI * 1.5, Math.PI * 2); ctx.stroke();
}

/** Full field + 2 vertical dashed lines at 1/3 and 2/3 (vertical thirds / channels) */
function drawFullVThirdsField(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  drawFullField(ctx, px, py, fw, fh);

  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([10, 8]);

  ctx.beginPath();
  ctx.moveTo(px + fw / 3, py);
  ctx.lineTo(px + fw / 3, py + fh);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(px + (fw * 2) / 3, py);
  ctx.lineTo(px + (fw * 2) / 3, py + fh);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
}

/** Full field + 3 horizontal dashed lines at 1/4, center, 3/4 */
function drawFullHZonesField(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  drawFullField(ctx, px, py, fw, fh);

  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([10, 8]);

  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.25);
  ctx.lineTo(px + fw, py + fh * 0.25);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.5);
  ctx.lineTo(px + fw, py + fh * 0.5);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.75);
  ctx.lineTo(px + fw, py + fh * 0.75);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
}

/** Full field + 2 vertical dashed (1/3, 2/3) + 3 horizontal dashed (1/4, center, 3/4) */
function drawFullGridField(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  drawFullField(ctx, px, py, fw, fh);

  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([10, 8]);

  // Vertical dashed lines at 1/3 and 2/3
  ctx.beginPath();
  ctx.moveTo(px + fw / 3, py);
  ctx.lineTo(px + fw / 3, py + fh);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(px + (fw * 2) / 3, py);
  ctx.lineTo(px + (fw * 2) / 3, py + fh);
  ctx.stroke();

  // Horizontal dashed lines at 1/4, center, 3/4
  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.25);
  ctx.lineTo(px + fw, py + fh * 0.25);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.5);
  ctx.lineTo(px + fw, py + fh * 0.5);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.75);
  ctx.lineTo(px + fw, py + fh * 0.75);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
}

/** Full field + 2 horizontal dashed lines at 1/4 and 3/4 (wing corridors) */
function drawFullHCorridorsField(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  drawFullField(ctx, px, py, fw, fh);

  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([10, 8]);

  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.25);
  ctx.lineTo(px + fw, py + fh * 0.25);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(px, py + fh * 0.75);
  ctx.lineTo(px + fw, py + fh * 0.75);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
}

// ────── Field Measurements Overlay ──────

export function drawFieldMeasurements(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  fieldType: FieldType
) {
  const px = 22;
  const padTop = 38;
  const padBot = 22;
  const fw = width - px * 2;
  const fh = height - padTop - padBot;

  // Only draw full-field measurements for field types based on a full field
  if (fieldType === 'half') {
    drawHalfFieldMeasurements(ctx, px, padTop, fw, fh);
  } else {
    drawFullFieldMeasurements(ctx, px, padTop, fw, fh);
  }
}

function drawMeasureLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  bg: string,
  textColor: string = '#fff',
  fontSize: number = 11
) {
  ctx.font = `bold ${fontSize}px sans-serif`;
  const m = ctx.measureText(text);
  const pad = 3;
  const w = m.width + pad * 2;
  const h = fontSize + pad * 2;
  ctx.fillStyle = bg;
  ctx.fillRect(Math.round(x - w / 2), Math.round(y - h / 2), Math.round(w), Math.round(h));
  ctx.fillStyle = textColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x, y);
}

function drawDimArrow(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number,
  x2: number, y2: number,
  color: string
) {
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  const angle = Math.atan2(y2 - y1, x2 - x1);
  const hl = 6;
  // Start arrowhead
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x1 + hl * Math.cos(angle - 0.4), y1 + hl * Math.sin(angle - 0.4));
  ctx.lineTo(x1 + hl * Math.cos(angle + 0.4), y1 + hl * Math.sin(angle + 0.4));
  ctx.fill();
  // End arrowhead
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - hl * Math.cos(angle - 0.4), y2 - hl * Math.sin(angle - 0.4));
  ctx.lineTo(x2 - hl * Math.cos(angle + 0.4), y2 - hl * Math.sin(angle + 0.4));
  ctx.fill();
}

function drawFullFieldMeasurements(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  const cyan = '#00bcd4';
  const yellow = '#c8b400';
  const red = '#d32f2f';
  const orange = '#e68a00';

  // Field geometry (must match drawFullField)
  const paW = fw * 0.16;
  const paH = fh * 0.62;
  const gaW = fw * 0.06;
  const gaH = fh * 0.3;
  const goalH = fh * 0.14;
  const centerR = fh * 0.2;

  // Left side positions
  const lPaRight = px + paW;
  const lGaRight = px + gaW;
  const lPenSpotX = px + paW * 0.72;
  const lPaTop = py + (fh - paH) / 2;
  const lPaBot = lPaTop + paH;
  const lGaTop = py + (fh - gaH) / 2;
  const lGaBot = lGaTop + gaH;
  const lGoalTop = py + (fh - goalH) / 2;
  const lGoalBot = lGoalTop + goalH;

  // Right side positions
  const rPaLeft = px + fw - paW;
  const rPaTop = lPaTop;
  const rPaBot = lPaBot;

  // ── Overall dimensions (cyan) ──

  // 105 m – full field length (arrow BELOW field)
  const arrowY = py + fh + 12;
  drawDimArrow(ctx, px, arrowY, px + fw, arrowY, cyan);
  drawMeasureLabel(ctx, '105 m', px + fw / 2, arrowY, cyan);

  // 68 m – field width (inside field, along left edge, label at top)
  const arrowX = px + 8;
  drawDimArrow(ctx, arrowX, py + 2, arrowX, py + fh - 2, cyan);
  drawMeasureLabel(ctx, '68 m', arrowX + 22, py + 14, cyan);

  // 52.5 – each half (inside field near bottom)
  const halfY = py + fh - 12;
  drawMeasureLabel(ctx, '52.5', px + fw / 4, halfY, cyan, '#fff', 9);
  drawMeasureLabel(ctx, '52.5', px + (fw * 3) / 4, halfY, cyan, '#fff', 9);

  // ── Center circle (yellow) ──
  drawDimArrow(ctx, px + fw / 2, py + fh / 2, px + fw / 2 + centerR, py + fh / 2, yellow);
  drawMeasureLabel(ctx, '9.15 m', px + fw / 2 + centerR / 2, py + fh / 2 - 14, yellow, '#000', 9);

  // ══════════════════════════════════════
  // LEFT GOAL SIDE – goal & goal area details
  // ══════════════════════════════════════

  // 11 m – left penalty spot distance (orange)
  drawDimArrow(ctx, px, py + fh / 2 + 16, lPenSpotX, py + fh / 2 + 16, orange);
  drawMeasureLabel(ctx, '11 m', (px + lPenSpotX) / 2, py + fh / 2 + 30, orange, '#000', 9);

  // 5.50 m – goal area depth (yellow, arrow above goal area)
  const lGaDepthY = lGaTop - 10;
  drawDimArrow(ctx, px, lGaDepthY, lGaRight, lGaDepthY, yellow);
  drawMeasureLabel(ctx, '5.50 m', (px + lGaRight) / 2, lGaDepthY - 12, yellow, '#000', 9);

  // 18 m – goal area width (yellow, inside goal area, label at center)
  const lGaWidthX = lGaRight - 10;
  drawDimArrow(ctx, lGaWidthX, lGaTop, lGaWidthX, lGaBot, yellow);
  drawMeasureLabel(ctx, '18 m', lGaWidthX + 22, py + fh / 2, yellow, '#000', 9);

  // 7.32 m – goal width (yellow, at the goal, label above goal)
  const lGoalX = px + 4;
  drawDimArrow(ctx, lGoalX, lGoalTop, lGoalX, lGoalBot, yellow);
  drawMeasureLabel(ctx, '7.32 m', lGoalX + 30, lGoalTop + 14, yellow, '#000', 8);

  // 2.44 m – goal height (yellow, label below goal)
  drawMeasureLabel(ctx, '2.44 m', lGoalX + 30, lGoalBot + 12, yellow, '#000', 8);

  // ══════════════════════════════════════
  // RIGHT SIDE – penalty area details
  // ══════════════════════════════════════

  // 11 m – right penalty spot distance (orange)
  const rPenSpotX = px + fw - paW * 0.72;
  drawDimArrow(ctx, px + fw, py + fh / 2 + 16, rPenSpotX, py + fh / 2 + 16, orange);
  drawMeasureLabel(ctx, '11 m', (px + fw + rPenSpotX) / 2, py + fh / 2 + 30, orange, '#000', 9);

  // 16.50 m – penalty area depth (red, below penalty area)
  const paDepthY = rPaBot + 14;
  drawDimArrow(ctx, rPaLeft, paDepthY, px + fw, paDepthY, red);
  drawMeasureLabel(ctx, '16.50 m', (rPaLeft + px + fw) / 2, paDepthY, red);

  // 40.32 m – penalty area width (red, inside penalty area)
  const paWidthX = rPaLeft + 10;
  drawDimArrow(ctx, paWidthX, rPaTop, paWidthX, rPaBot, red);
  drawMeasureLabel(ctx, '40.32 m', paWidthX + 28, py + fh / 2, red);

  // 14 m – sideline to penalty area top (red)
  const sideX = px + fw - 8;
  drawDimArrow(ctx, sideX, py + 2, sideX, rPaTop, red);
  drawMeasureLabel(ctx, '14 m', sideX - 20, (py + rPaTop) / 2, red, '#fff', 9);

  // 14 m – sideline to penalty area bottom (red)
  drawDimArrow(ctx, sideX, rPaBot, sideX, py + fh - 2, red);
  drawMeasureLabel(ctx, '14 m', sideX - 20, (rPaBot + py + fh) / 2, red, '#fff', 9);

  // 11 m – PA to GA gap top (yellow)
  const rGaTop = lGaTop;
  const rGaBot = lGaBot;
  const gapX = px + fw - 20;
  drawDimArrow(ctx, gapX, rPaTop + 2, gapX, rGaTop - 2, yellow);
  drawMeasureLabel(ctx, '11 m', gapX - 18, (rPaTop + rGaTop) / 2, yellow, '#000', 9);

  // 11 m – PA to GA gap bottom (yellow)
  drawDimArrow(ctx, gapX, rGaBot + 2, gapX, rPaBot - 2, yellow);
  drawMeasureLabel(ctx, '11 m', gapX - 18, (rGaBot + rPaBot) / 2, yellow, '#000', 9);

  // Reset styles
  ctx.setLineDash([]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.textAlign = 'start';
  ctx.textBaseline = 'alphabetic';
}

function drawHalfFieldMeasurements(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  const cyan = '#00bcd4';
  const yellow = '#c8b400';
  const orange = '#e68a00';

  // Half-field geometry (must match drawHalfField)
  const paW = fw * 0.25;
  const paH = fh * 0.62;
  const gaW = fw * 0.09;
  const gaH = fh * 0.3;
  const goalH = fh * 0.14;
  const penSpotX = px + paW * 0.72;

  const paTop = py + (fh - paH) / 2;
  const gaTop = py + (fh - gaH) / 2;
  const goalTop = py + (fh - goalH) / 2;
  const goalBot = goalTop + goalH;

  // 52.5 m – half length (BELOW field)
  const arrowY = py + fh + 12;
  drawDimArrow(ctx, px, arrowY, px + fw, arrowY, cyan);
  drawMeasureLabel(ctx, '52.5 m', px + fw / 2, arrowY, cyan);

  // 68 m – width (INSIDE field, left edge)
  const arrowX = px + 8;
  drawDimArrow(ctx, arrowX, py + 2, arrowX, py + fh - 2, cyan);
  drawMeasureLabel(ctx, '68 m', arrowX + 20, py + fh / 2, cyan);

  // 9.15 m – center circle radius
  const centerR = fh * 0.2;
  drawMeasureLabel(ctx, '9.15 m', px + fw - centerR / 2, py + fh / 2 - 14, yellow, '#000', 9);

  // 11 m – penalty spot
  drawDimArrow(ctx, px, py + fh / 2 + 16, penSpotX, py + fh / 2 + 16, orange);
  drawMeasureLabel(ctx, '11 m', (px + penSpotX) / 2, py + fh / 2 + 30, orange, '#000', 9);

  // 16.50 m – penalty area depth (inside field near bottom of PA)
  const paDepthY = paTop - 12;
  drawDimArrow(ctx, px, paDepthY, px + paW, paDepthY, cyan);
  drawMeasureLabel(ctx, '16.50 m', px + paW / 2, paDepthY, cyan, '#fff', 9);

  // 40.32 m – penalty area width
  drawMeasureLabel(ctx, '40.32 m', px + paW + 30, py + fh / 2, cyan, '#fff', 9);

  // 5.50 m – goal area depth (inside field)
  const gaDepthY = gaTop + 12;
  drawDimArrow(ctx, px, gaDepthY, px + gaW, gaDepthY, yellow);
  drawMeasureLabel(ctx, '5.50 m', px + gaW + 24, gaDepthY, yellow, '#000', 9);

  // 7.32 m – goal (inside field)
  const goalX = px + 4;
  drawDimArrow(ctx, goalX, goalTop, goalX, goalBot, yellow);
  drawMeasureLabel(ctx, '7.32 m', goalX + 24, goalTop + 10, yellow, '#000', 9);

  // Reset
  ctx.setLineDash([]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.textAlign = 'start';
  ctx.textBaseline = 'alphabetic';
}

function drawGoalNet(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number
) {
  // Goal frame
  ctx.fillStyle = 'rgba(255,255,255,0.15)';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 2.5;
  ctx.strokeRect(x, y, w, h);

  // Net pattern
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.lineWidth = 0.5;
  const gap = 5;
  for (let i = gap; i < h; i += gap) {
    ctx.beginPath();
    ctx.moveTo(x, y + i);
    ctx.lineTo(x + w, y + i);
    ctx.stroke();
  }
  for (let i = gap; i < w; i += gap) {
    ctx.beginPath();
    ctx.moveTo(x + i, y);
    ctx.lineTo(x + i, y + h);
    ctx.stroke();
  }
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
}

/**
 * A futsal court, drawn to the real proportions of a 40x20m pitch.
 *
 * The markings are what distinguish it from a small football pitch, and they
 * are not cosmetic — a coach setting up a session needs to see them. There is
 * no rectangular penalty box: the goal area is a "D" made of two quarter
 * circles of 6m struck from each post and joined by a straight line. There are
 * two penalty marks, at 6m and at 10m. The goals are 3m wide, not 7.32m.
 */
function drawFutsalCourt(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  fw: number,
  fh: number
) {
  const cy = py + fh / 2;
  // Everything below is expressed as a fraction of the real 40x20 court, so the
  // proportions stay right whatever size the canvas is drawn at.
  const mx = fw / 40; // one metre along the length
  const my = fh / 20; // one metre across the width

  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 2;
  ctx.setLineDash([]);

  // Halfway line
  ctx.beginPath();
  ctx.moveTo(px + fw / 2, py);
  ctx.lineTo(px + fw / 2, py + fh);
  ctx.stroke();

  // Centre circle: 3m radius
  ctx.beginPath();
  ctx.arc(px + fw / 2, cy, 3 * my, 0, Math.PI * 2);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(px + fw / 2, cy, 2.5, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fill();

  const goalHalf = 1.5 * my;  // 3m goal, half of it either side of centre
  const areaR = 6 * my;       // 6m quarter circles

  // The goal area "D" at one end. `dir` is +1 for the left goal, -1 for the right.
  const drawArea = (goalX: number, dir: 1 | -1) => {
    const topPostY = cy - goalHalf;
    const botPostY = cy + goalHalf;

    ctx.beginPath();
    if (dir === 1) {
      // Quarter circle from the goal line round to a line parallel to it.
      ctx.arc(goalX, topPostY, areaR, -Math.PI / 2, 0);
      ctx.lineTo(goalX + areaR, botPostY);
      ctx.arc(goalX, botPostY, areaR, 0, Math.PI / 2);
    } else {
      ctx.arc(goalX, topPostY, areaR, -Math.PI / 2, Math.PI, true);
      ctx.lineTo(goalX - areaR, botPostY);
      ctx.arc(goalX, botPostY, areaR, Math.PI, Math.PI / 2, true);
    }
    ctx.stroke();

    // First penalty mark, on the 6m line.
    ctx.beginPath();
    ctx.arc(goalX + dir * 6 * mx, cy, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fill();

    // Second penalty mark at 10m — the one futsal has and football does not.
    ctx.beginPath();
    ctx.arc(goalX + dir * 10 * mx, cy, 2.5, 0, Math.PI * 2);
    ctx.fill();

    // Goal: 3m wide, so visibly narrower than a football goal.
    const goalDepth = 10;
    drawGoalNet(
      ctx,
      dir === 1 ? goalX - goalDepth : goalX,
      cy - goalHalf,
      goalDepth,
      goalHalf * 2
    );
  };

  drawArea(px, 1);
  drawArea(px + fw, -1);

  // Corner arcs: 25cm, small but present on a real court.
  const cornerR = 0.25 * my * 4;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 2;
  const corners: [number, number, number, number][] = [
    [px, py, 0, Math.PI / 2],
    [px + fw, py, Math.PI / 2, Math.PI],
    [px + fw, py + fh, Math.PI, Math.PI * 1.5],
    [px, py + fh, Math.PI * 1.5, Math.PI * 2],
  ];
  for (const [x, y, a0, a1] of corners) {
    ctx.beginPath();
    ctx.arc(x, y, cornerR, a0, a1);
    ctx.stroke();
  }

  // Substitution zones: 5m either side of the halfway line on the bench
  // touchline, marked by the short lines players must change within.
  ctx.lineWidth = 2;
  for (const offset of [-5, -2, 2, 5]) {
    const x = px + fw / 2 + offset * mx;
    ctx.beginPath();
    ctx.moveTo(x, py + fh);
    ctx.lineTo(x, py + fh + 6);
    ctx.stroke();
  }
}
